import React, { useCallback, useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

function fmtUSD(v) {
  const n = Number(v || 0);
  return "$" + n.toFixed(4);
}
function fmtInt(v) {
  return Number(v || 0).toLocaleString();
}
function fmtMs(v) {
  if (v == null) return "—";
  if (v < 1000) return `${v}ms`;
  return `${(v / 1000).toFixed(1)}s`;
}
function fmtWhen(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString();
}
function shortId(v) {
  return v ? v.slice(0, 8) : "—";
}

function KpiTile({ label, value, sub, testId, tone }) {
  return (
    <div className="bo-card" data-testid={testId} style={{
      padding: 18,
      borderTop: tone === "warn" ? "3px solid #c48411" :
                  tone === "danger" ? "3px solid var(--bo-danger)" :
                  tone === "success" ? "3px solid var(--bo-success)" :
                  "3px solid var(--bo-accent)",
    }}>
      <div className="bo-eyebrow" style={{ marginBottom: 6 }}>{label}</div>
      <div className="bo-serif" style={{ fontSize: 28, letterSpacing: "-0.01em" }}>
        {value}
      </div>
      {sub && (
        <div style={{ color: "var(--bo-muted)", fontSize: 12, marginTop: 4 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function DailyCostBars({ days }) {
  if (!days || days.length === 0) {
    return <EmptyState title="No invocations yet in this period." description="Once you generate copy, cost-per-day will render here." />;
  }
  const max = Math.max(...days.map((d) => d.cost_usd), 0.001);
  return (
    <div style={{ display: "grid", gap: 6 }} data-testid="daily-cost-bars">
      {days.map((d) => {
        const pct = Math.min(100, (d.cost_usd / max) * 100);
        return (
          <div key={d.date} style={{ display: "grid", gridTemplateColumns: "110px 1fr 100px 80px", gap: 12, alignItems: "center", fontSize: 13 }}>
            <div style={{ fontFamily: "var(--bo-mono)", color: "var(--bo-muted)" }}>{d.date}</div>
            <div style={{ background: "var(--bo-panel-alt)", height: 22, borderRadius: 5, position: "relative", overflow: "hidden" }}>
              <div style={{
                width: `${pct}%`,
                background: "linear-gradient(90deg, var(--bo-accent) 0%, var(--bo-accent-hover) 100%)",
                height: "100%",
                transition: "width 200ms",
              }} />
            </div>
            <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtUSD(d.cost_usd)}</div>
            <div style={{ textAlign: "right", color: "var(--bo-muted)", fontSize: 12 }}>{fmtInt(d.invocations)} calls</div>
          </div>
        );
      })}
    </div>
  );
}

export default function GovernanceDashboard() {
  const [days, setDays] = useState(30);
  const [state, setState] = useState({ loading: true, error: null });
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState(null);
  const [models, setModels] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [audit, setAudit] = useState([]);
  const [invocations, setInvocations] = useState([]);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [s, dRes, m, a, au, iv] = await Promise.all([
        api.get(`/governance/summary?days=${days}`),
        api.get(`/governance/costs/daily?days=${Math.min(days, 30)}`),
        api.get(`/governance/models?days=${days}`),
        api.get(`/governance/approvals?limit=15`),
        api.get(`/governance/audit?limit=30`),
        api.get(`/governance/invocations?limit=15`),
      ]);
      setSummary(s.data);
      setDaily(dRes.data);
      setModels(m.data.models || []);
      setApprovals(a.data.approvals || []);
      setAudit(au.data.events || []);
      setInvocations(iv.data.invocations || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    if (!summary) return [];
    const inv = summary.invocations || {};
    const gen = summary.generations || {};
    const ap = summary.approvals || {};
    const g = summary.guardian_distribution || {};
    return [
      { label: "Total spend", value: fmtUSD(inv.cost_usd), sub: `${fmtInt(inv.total)} LLM calls · ${fmtMs(inv.avg_latency_ms)} avg`, testId: "kpi-spend" },
      { label: "Generations", value: fmtInt(gen.total), sub: `${fmtInt(gen.by_status?.succeeded || 0)} succeeded · ${fmtInt(gen.by_status?.failed || 0)} failed`, testId: "kpi-generations" },
      { label: "Approvals", value: fmtInt(ap.approved || 0), sub: `${fmtInt(ap.rejected || 0)} rejected`, testId: "kpi-approvals", tone: "success" },
      { label: "Guardian: block", value: fmtInt(g.block || 0), sub: `${fmtInt(g.warn || 0)} warn · ${fmtInt(g.pass || 0)} pass · ${fmtInt(g.pending || 0)} pending`, testId: "kpi-guardian-block", tone: (g.block || 0) > 0 ? "warn" : undefined },
    ];
  }, [summary]);

  return (
    <Shell title="Governance">
      <div className="bo-eyebrow">Operate</div>
      <h1 className="bo-serif" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }} data-testid="dashboard-title">
        The trail behind every decision.
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 720, marginBottom: 20 }} data-testid="dashboard-lede">
        Cost, latency, Guardian verdicts, approvals, and the full audit trail — the substrate on which every Charter-compliance question is answered.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 20 }}>
        <span className="bo-eyebrow">Window</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`window-${d}`}
            className="bo-btn bo-btn-ghost"
            style={{
              padding: "4px 12px",
              fontSize: 12.5,
              background: days === d ? "var(--bo-accent-soft)" : "transparent",
              borderColor: days === d ? "var(--bo-accent)" : "var(--bo-line)",
            }}
          >
            {d} days
          </button>
        ))}
      </div>

      <ErrorBanner error={state.error} testId="dashboard-error" />
      {state.loading && <LoadingCard label="Loading governance data…" />}

      {!state.loading && summary && (
        <>
          {/* KPI grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 24 }}>
            {kpis.map((k) => <KpiTile key={k.label} {...k} />)}
          </div>

          {/* Daily cost */}
          <div className="bo-card" data-testid="daily-cost-card" style={{ marginBottom: 20 }}>
            <div className="bo-eyebrow">Trend</div>
            <h2 className="bo-card-title" style={{ marginBottom: 14 }}>Cost by day</h2>
            <DailyCostBars days={daily?.days || []} />
          </div>

          {/* Model breakdown */}
          <div className="bo-card" data-testid="models-card" style={{ marginBottom: 20 }}>
            <div className="bo-eyebrow">Providers</div>
            <h2 className="bo-card-title" style={{ marginBottom: 14 }}>Model breakdown</h2>
            {models.length === 0 && <EmptyState title="No model invocations yet." />}
            {models.length > 0 && (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 90px 100px 90px 80px", gap: 12, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--bo-muted)", padding: "0 8px" }}>
                  <div>Model</div><div>Capability</div><div style={{ textAlign: "right" }}>Calls</div><div style={{ textAlign: "right" }}>Cost</div><div style={{ textAlign: "right" }}>Tokens</div><div style={{ textAlign: "right" }}>Avg lat.</div>
                </div>
                {models.map((m, i) => (
                  <div key={i} data-testid={`model-row-${m.model}`} style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1fr 90px 100px 90px 80px",
                    gap: 12, alignItems: "center",
                    padding: "10px 8px",
                    background: "var(--bo-panel-alt)",
                    borderRadius: 6,
                    fontSize: 13.5,
                  }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{m.model}</div>
                      <div style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>{m.provider}</div>
                    </div>
                    <div style={{ fontFamily: "var(--bo-mono)", fontSize: 12 }}>{m.capability}</div>
                    <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtInt(m.invocations)}</div>
                    <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtUSD(m.cost_usd)}</div>
                    <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--bo-muted)" }}>{fmtInt(m.input_tokens + m.output_tokens)}</div>
                    <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--bo-muted)" }}>{fmtMs(m.avg_latency_ms)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Approvals */}
          <div className="bo-card" data-testid="approvals-card" style={{ marginBottom: 20 }}>
            <div className="bo-eyebrow">Reviews</div>
            <h2 className="bo-card-title" style={{ marginBottom: 14 }}>Recent approval events</h2>
            {approvals.length === 0 && <EmptyState title="No approval events yet." />}
            {approvals.length > 0 && (
              <div style={{ display: "grid", gap: 8 }}>
                {approvals.map((a) => {
                  const tone = a.action === "approved" ? undefined : "danger";
                  return (
                    <div key={a.id} data-testid={`approval-row-${a.id}`} style={{
                      padding: "10px 12px",
                      background: "var(--bo-panel-alt)",
                      border: "1px solid var(--bo-line)",
                      borderRadius: 6,
                      display: "grid",
                      gridTemplateColumns: "90px 1fr auto",
                      gap: 12,
                      alignItems: "start",
                    }}>
                      <Pill tone={tone}>{a.action}</Pill>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontFamily: "var(--bo-mono)", color: "var(--bo-muted)" }}>
                          version {shortId(a.creative_version_id)} · asset {shortId(a.asset_id)}
                        </div>
                        {a.reason && <div style={{ fontSize: 13, color: "var(--bo-ink-soft)", marginTop: 4 }}><em>reason:</em> {a.reason}</div>}
                        {a.override_note && <div style={{ fontSize: 13, color: "#8b6417", marginTop: 4 }}><em>override:</em> {a.override_note}</div>}
                        {a.guardian_status_at_approval && <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 3 }}>guardian at approval: {a.guardian_status_at_approval}</div>}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--bo-muted)", textAlign: "right", whiteSpace: "nowrap" }}>{fmtWhen(a.created_at)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Audit log */}
          <div className="bo-card" data-testid="audit-card" style={{ marginBottom: 20 }}>
            <div className="bo-eyebrow">Trail</div>
            <h2 className="bo-card-title" style={{ marginBottom: 14 }}>Audit log</h2>
            {audit.length === 0 && <EmptyState title="No audit events yet." />}
            {audit.length > 0 && (
              <div style={{ display: "grid", gap: 4, fontSize: 12.5, fontFamily: "var(--bo-mono)" }}>
                {audit.map((e) => (
                  <div key={e.id} data-testid={`audit-row-${e.id}`} style={{
                    display: "grid",
                    gridTemplateColumns: "150px 200px 1fr",
                    gap: 12,
                    padding: "6px 8px",
                    borderBottom: "1px solid var(--bo-line)",
                  }}>
                    <div style={{ color: "var(--bo-muted)" }}>{fmtWhen(e.created_at)}</div>
                    <div style={{ color: "var(--bo-ink)" }}>{e.action}</div>
                    <div style={{ color: "var(--bo-muted)" }}>
                      <span>{e.object_type}:{shortId(e.object_id)}</span>
                      {e.actor_user_id && <span style={{ marginLeft: 8 }}>· by {shortId(e.actor_user_id)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent invocations */}
          <div className="bo-card" data-testid="invocations-card">
            <div className="bo-eyebrow">Raw</div>
            <h2 className="bo-card-title" style={{ marginBottom: 14 }}>Recent LLM calls</h2>
            {invocations.length === 0 && <EmptyState title="No invocations yet." />}
            {invocations.length > 0 && (
              <div style={{ display: "grid", gap: 6, fontSize: 12.5 }}>
                {invocations.map((iv) => (
                  <div key={iv.id} data-testid={`invocation-row-${iv.id}`} style={{
                    display: "grid",
                    gridTemplateColumns: "140px 150px 80px 90px 90px 1fr",
                    gap: 12,
                    padding: "8px",
                    background: iv.success === false ? "#fdf1ec" : "var(--bo-panel-alt)",
                    borderRadius: 5,
                    fontFamily: "var(--bo-mono)",
                  }}>
                    <div style={{ color: "var(--bo-muted)" }}>{fmtWhen(iv.created_at)}</div>
                    <div>{iv.model}</div>
                    <div style={{ textAlign: "right" }}>{fmtUSD(iv.cost_usd)}</div>
                    <div style={{ textAlign: "right", color: "var(--bo-muted)" }}>{fmtMs(iv.latency_ms)}</div>
                    <div style={{ textAlign: "right", color: "var(--bo-muted)" }}>{fmtInt(iv.input_tokens + iv.output_tokens)} tok</div>
                    <div style={{ color: iv.success === false ? "var(--bo-danger)" : "var(--bo-muted)" }}>
                      {iv.success === false ? `FAIL: ${iv.error?.message || "unknown"}` : iv.finish_reason || "ok"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </Shell>
  );
}
